/* eslint-disable react-hooks/rules-of-hooks */
import { FormApi } from 'final-form'
import setFieldTouched from 'final-form-set-field-touched'
import React from 'react'
import { Form } from 'react-final-form'
import { OnChange } from 'react-final-form-listeners'
import { animated, useTransition } from 'react-spring'

import { Alert } from '@cinch-labs/design-system'
import { Primary } from '@cinch/oxd-components/button/src'
import Panel from '@cinch/oxd-components/panel/src'

import getAllDependants from '../../stages/manual-lookup/get-all-dependants'
import { Journey } from '../../types'
import Question from '../question/question.component'
import buildInitialValues from './build-initial-values'

import * as styles from './journey.module.css'

export interface JourneyBuilderProps {
  journey?: Journey
  submitText: string

  onFieldChange?: (field: string, form: FormApi) => any
  onSubmit: (formData: any) => Promise<any>
}

/**
 * This component builds the main journey panels, each page gets its own panel
 */
const JourneyBuilder: React.FC<JourneyBuilderProps> = ({ journey, onSubmit, submitText, onFieldChange }) => {
  if (!journey) {
    return null
  }
  const initialValues = buildInitialValues(journey)
  const [currentIndex, setIndex] = React.useState<number[]>([0])
  const transitions = useTransition(currentIndex, (page) => page, {
    from: { transform: 'perspective(600px) translate3d(20%,0,-30px)', opacity: 0 },
    enter: { transform: 'perspective(600px) translate3d(0,0px,0)', opacity: 1 },
    leave: {
      transform: 'perspective(600px) translate3d(-20%,0,-30px)',
      position: 'absolute' as 'absolute',
      opacity: 0,
    },
  })
  const nextIndex = currentIndex[0] + 1
  const isLastPage = nextIndex === journey.journeyPages.length
  const processClick = (formData: any) => {
    if (isLastPage) {
      return onSubmit(formData)
    } else {
      setIndex([nextIndex])
    }
  }

  return (
    <Form
      onSubmit={processClick}
      initialValues={initialValues}
      mutators={{ setFieldTouched } as any}
      render={({ handleSubmit, submitting, submitError, form, invalid }) => (
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.pages}>
            {transitions.map(({ item, props, key }) => (
              <animated.div style={props as any} key={key} className={styles.page}>
                <Panel>
                  <fieldset className={styles.fieldset}>
                    <legend>
                      {journey.journeyPages[item].pageDisplayContent[0].pageTitle ||
                        journey.journeyPages[item].pageDisplayContent[0].pageDescription}
                    </legend>
                    {journey.journeyPages[item].pageQuestions.map((question) => (
                      <React.Fragment key={question.questionParameterId}>
                        <Question
                          question={question}
                          validateFields={
                            onFieldChange && getAllDependants(journey.journeyPages[item].pageQuestions, question)
                          }
                        />
                        {onFieldChange && (
                          <OnChange name={question.questionParameterName}>
                            {() => onFieldChange(question.questionParameterName, form)}
                          </OnChange>
                        )}
                      </React.Fragment>
                    ))}
                  </fieldset>

                  <div className={styles.buttonContainer}>
                    <Primary
                      disabled={invalid && !submitError}
                      oxdClassName={styles.button}
                      type="submit"
                      isLoading={submitting}
                      oxford={true}
                    >
                      {isLastPage ? submitText : 'Continue'}
                    </Primary>
                  </div>
                </Panel>
              </animated.div>
            ))}
          </div>
          {submitError && (
            <div className={styles.alert}>
              <Alert oxford={true}>{submitError}</Alert>
            </div>
          )}
        </form>
      )}
    />
  )
}

export default JourneyBuilder
